import { useState } from 'react';
import { Link } from '@tanstack/react-router';

import { usdText } from '../../shared/lib/format';
import { Meter } from '../../shared/ui/meter';
import { PanelEmpty, SectionPanel } from '../../shared/ui/section-panel';
import { Segmented } from '../../shared/ui/segmented';
import { Table, type IColumn } from '../../shared/ui/table';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import type { TAiUsageWindow } from '../../entities/organization';
import { useAiUsage } from './use-ai-budget';

const WINDOWS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 d' },
  { value: '30d', label: '30 d' },
] as const;

const TITLE: Record<TAiUsageWindow, string> = {
  today: 'Spent today, by flow',
  '7d': 'Spent in the last 7 days, by flow',
  '30d': 'Spent in the last 30 days, by flow',
};

const SHARE: Record<TAiUsageWindow, string> = {
  today: 'Share of today',
  '7d': 'Share of 7 d',
  '30d': 'Share of 30 d',
};

const STEP_WORDS: Record<string, string> = {
  'ai.classify': 'AI Classify',
  'ai.extract': 'AI Extract',
  'ai.generate': 'AI Generate',
  'ai.agent': 'Agent',
};

interface ISpendRow {
  key: string;
  title: string;
  sub: string;
  flowId?: string;
  share: number;
  spent: number;
  calls: number;
  agentRuns: number | null;
}

const columns = (span: TAiUsageWindow): IColumn<ISpendRow>[] => [
  {
    key: 'flow',
    header: 'Flow · AI step',
    width: 'grow',
    ui: true,
    render: (row) => (
      <span className="flex min-w-0 flex-col">
        {row.flowId ? (
          <Link
            to="/flows/$flowId"
            params={{ flowId: row.flowId }}
            className="truncate text-primary hover:underline"
          >
            {row.title}
          </Link>
        ) : (
          <span className="truncate text-primary">{row.title}</span>
        )}
        <span className="truncate font-mono text-micro text-faint">{row.sub}</span>
      </span>
    ),
  },
  {
    key: 'share',
    header: SHARE[span],
    width: 'w-col-spend',
    ui: true,
    render: (row) => <Meter value={row.share} tone="motion" />,
  },
  {
    key: 'spent',
    header: 'Spent',
    width: 'w-col-size',
    align: 'right',
    render: (row) => usdText(row.spent),
  },
  {
    key: 'calls',
    header: 'Calls',
    width: 'w-col-growth',
    align: 'right',
    render: (row) =>
      [
        row.agentRuns !== null
          ? `${row.agentRuns.toLocaleString('en-US')} ${row.agentRuns === 1 ? 'run' : 'runs'}`
          : null,
        row.calls > 0
          ? `${row.calls.toLocaleString('en-US')} ${row.calls === 1 ? 'call' : 'calls'}`
          : null,
      ]
        .filter(Boolean)
        .join(' · ') || '—',
  },
];

/** Board S4: what each flow's AI steps cost, today or over 7 / 30 days. */
export const SpendTable = () => {
  const [span, setSpan] = useState<TAiUsageWindow>('today');
  const usage = useAiUsage(span);
  const data = usage.data;
  const total = data?.totalUsd ?? 0;
  const share = (usd: number) => (total > 0 ? (usd / total) * 100 : 0);

  const rows: ISpendRow[] = data
    ? [
        ...data.rows.map((row) => ({
          key: `${row.flowId}:${row.nodeKey}`,
          title: row.flowName ?? 'A deleted flow',
          sub:
            row.nodeType === 'ai.agent'
              ? `Agent · ${row.nodeName ?? row.nodeKey}`
              : `${row.nodeName ?? row.nodeKey}${row.nodeType && STEP_WORDS[row.nodeType] && row.nodeName !== STEP_WORDS[row.nodeType] ? ` · ${STEP_WORDS[row.nodeType]}` : ''}`,
          flowId: row.flowName ? row.flowId : undefined,
          share: share(row.costUsd),
          spent: row.costUsd,
          calls: row.calls,
          agentRuns: row.agentRuns,
        })),
        {
          key: 'tests',
          title: 'Tests of drafts',
          sub: 'all flows',
          share: share(data.tests.costUsd),
          spent: data.tests.costUsd,
          calls: data.tests.calls,
          agentRuns: null,
        },
      ]
    : [];

  return (
    <SectionPanel
      title={TITLE[span]}
      actions={<Segmented aria-label="Window" segments={WINDOWS} value={span} onChange={setSpan} />}
    >
      {usage.isPending && <LoadingState rows={4} className="p-input" />}
      {usage.isError && (
        <div className="p-input">
          <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />
        </div>
      )}
      {data && data.rows.length === 0 && data.tests.calls === 0 && (
        <PanelEmpty title="No AI step has run in this window">
          When a flow with a Classify, Extract, Generate or Agent step runs, it shows here with what
          it cost.
        </PanelEmpty>
      )}
      {data && (data.rows.length > 0 || data.tests.calls > 0) && (
        <Table
          tall
          layoutId="settings-ai-spend"
          columns={columns(span)}
          rows={rows}
          rowKey={(row) => row.key}
        />
      )}
    </SectionPanel>
  );
};
